/**
 * Citation Verification Tool - Paper Fetcher
 * 
 * Downloads papers and imports them to the dataspace
 */

import { createFileLogger, formatJSON } from '../../../utils/logger';
import { CommandExecutionContext } from '../../../types/commands';
import fetchService from '../../../services/fetchService';
import { API_BASE_URL } from '../../../api/config';
import {
  ResolutionResult,
  FetchResult,
  ResolvedPaper
} from './types';

const log = createFileLogger('VERIFY_CITATIONS_FETCHER');

/**
 * Fetch and import papers to dataspace
 */
export async function fetchAndImportPapers(
  resolutions: ResolutionResult[],
  manuscriptName: string,
  context: CommandExecutionContext,
  onProgress?: (progress: { completed: number; total: number }) => void
): Promise<FetchResult[]> {
  log.info('[fetchAndImportPapers] Starting fetch:', { count: resolutions.length });
  
  // Create dedicated folder for citations
  const folderName = `Citations from ${manuscriptName}`;
  const folderId = await createCitationsFolder(folderName, context);
  
  const results: FetchResult[] = [];
  
  for (let i = 0; i < resolutions.length; i++) {
    const resolution = resolutions[i];
    const reference = resolution.reference;
    
    // Skip if already in local dataspace
    if (resolution.localEntityId) {
      results.push({
        reference,
        resolvedPaper: resolution.resolvedMetadata!,
        status: 'full_text_imported',
        localEntityId: resolution.localEntityId,
        fetchSource: 'local_dataspace'
      });
      
      if (onProgress) {
        onProgress({ completed: i + 1, total: resolutions.length });
      }
      continue;
    }
    
    // Skip if unresolved
    if (resolution.status === 'unresolved' || !resolution.resolvedMetadata) {
      results.push({
        reference,
        resolvedPaper: resolution.resolvedMetadata!,
        status: 'failed',
        error: 'Reference could not be resolved'
      });
      
      if (onProgress) {
        onProgress({ completed: i + 1, total: resolutions.length });
      }
      continue;
    }
    
    // Try to fetch the paper
    try {
      const fetchResult = await fetchPaper(
        resolution.resolvedMetadata,
        folderId,
        context
      );
      results.push({
        reference,
        resolvedPaper: resolution.resolvedMetadata,
        ...fetchResult
      });
    } catch (error) {
      log.warn('[fetchAndImportPapers] Fetch failed:', error);
      results.push({
        reference,
        resolvedPaper: resolution.resolvedMetadata,
        status: 'failed',
        error: error instanceof Error ? error.message : 'Unknown error'
      });
    }
    
    if (onProgress) {
      onProgress({ completed: i + 1, total: resolutions.length });
    }
    
    // Small delay to be respectful
    await new Promise(resolve => setTimeout(resolve, 100));
  }
  
  return results;
}

/**
 * Create a folder for citation papers
 */
async function createCitationsFolder(
  folderName: string,
  context: CommandExecutionContext
): Promise<string> {
  log.info('[createCitationsFolder] 📁 Creating folder...');
  log.info(formatJSON({
    folderName,
    spaceId: context.spaceId,
    endpoint: `${API_BASE_URL}/files/create-group`
  }, '📤 Request'));
  
  try {
    const formData = new FormData();
    formData.append('folderName', folderName);
    formData.append('childEntities', JSON.stringify([]));
    
    // Add spaceId if available
    if (context.spaceId) {
      formData.append('spaceId', context.spaceId);
    }
    
    const response = await fetchService(`${API_BASE_URL}/files/create-group`, {
      method: 'POST',
      body: formData,
    });
    
    log.info(formatJSON(response, '📥 Response'));
    log.info('[createCitationsFolder] ✅ Folder created successfully');
    
    return response._id;
  } catch (error) {
    log.error('[createCitationsFolder] ❌ Failed to create folder');
    log.error(formatJSON({
      error: error instanceof Error ? {
        name: error.name,
        message: error.message,
        stack: error.stack
      } : error,
      folderName,
      spaceId: context.spaceId
    }, '⚠️ Error Context'));
    throw new Error(`Failed to create citations folder: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Fetch a single paper
 */
async function fetchPaper(
  paper: ResolvedPaper,
  folderId: string,
  context: CommandExecutionContext
): Promise<Omit<FetchResult, 'reference' | 'resolvedPaper'>> {
  log.info('[fetchPaper] Fetching paper:', {
    title: paper.title.substring(0, 50),
    hasPdf: !!paper.openAccessPdf,
    hasAbstract: !!paper.abstract,
    pdfUrl: paper.openAccessPdf || 'none',
    semanticScholarId: paper.semanticScholarId || 'none'
  });
  
  // Try to download PDF if available
  if (paper.openAccessPdf) {
    try {
      log.info('[fetchPaper] Attempting PDF download from:', paper.openAccessPdf);
      const pdfResult = await downloadAndImportPDF(paper, paper.openAccessPdf, folderId, context);
      log.info('[fetchPaper] ✅ PDF downloaded and imported successfully');
      return pdfResult;
    } catch (error) {
      log.warn('[fetchPaper] PDF download failed, falling back to abstract/metadata:', error);
    }
  } else {
    log.info('[fetchPaper] No open access PDF available for this paper');
  }
  
  // Fallback to abstract-only mode
  if (paper.abstract) {
    log.info('[fetchPaper] Creating abstract-only entity (no PDF available)');
    const entityId = await createAbstractOnlyEntity(paper, folderId, context);
    return {
      status: 'abstract_only',
      localEntityId: entityId,
      fetchSource: paper.semanticScholarId ? 'semantic_scholar' : 'doi_resolver'
    };
  }
  
  // Metadata only
  log.info('[fetchPaper] Creating metadata-only entity (no PDF or abstract available)');
  const entityId = await createMetadataOnlyEntity(paper, folderId, context);
  return {
    status: 'metadata_only',
    localEntityId: entityId,
    fetchSource: paper.semanticScholarId ? 'semantic_scholar' : 'doi_resolver'
  };
}

/**
 * Download PDF and import to dataspace
 */
async function downloadAndImportPDF(
  paper: ResolvedPaper,
  pdfUrl: string,
  folderId: string,
  context: CommandExecutionContext
): Promise<Omit<FetchResult, 'reference' | 'resolvedPaper'>> {
  log.info('[downloadAndImportPDF] 📄 Starting PDF download...');
  log.info(formatJSON({
    pdfUrl,
    paperTitle: paper.title.substring(0, 100),
    folderId,
    spaceId: context.spaceId
  }, '📤 Download Request'));
  
  try {
    // Download PDF through backend proxy to avoid CORS issues
    const downloadStart = Date.now();
    
    const { API_BASE_URL } = await import('../../../api/config');
    const authToken = localStorage.getItem('authToken');
    
    const response = await fetch(`${API_BASE_URL}/files/proxy-pdf-download`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
      },
      credentials: 'include',
      body: JSON.stringify({ url: pdfUrl })
    });
    
    const downloadTime = Date.now() - downloadStart;
    
    log.info(formatJSON({
      status: response.status,
      statusText: response.statusText,
      contentType: response.headers.get('content-type'),
      contentLength: response.headers.get('content-length'),
      downloadTimeMs: downloadTime
    }, '📥 Download Response'));
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`PDF download failed: ${response.status} ${response.statusText} - ${errorText}`);
    }
    
    const pdfBlob = await response.blob();
    
    log.info(formatJSON({
      blobSize: pdfBlob.size,
      blobType: pdfBlob.type,
      isValidSize: pdfBlob.size > 100,
      isPdfType: pdfBlob.type.includes('pdf')
    }, '📦 Downloaded Blob'));
    
    // Verify it's actually a PDF
    if (!pdfBlob.type.includes('pdf') && pdfBlob.size < 100) {
      throw new Error('Downloaded file does not appear to be a valid PDF');
    }
    
    log.info('[downloadAndImportPDF] ✅ PDF downloaded successfully');
    
    // Create File entity in dataspace with PDF content
    // FileService will automatically trigger the enrichment pipeline
    const entityId = await createPDFEntity(paper, pdfBlob, folderId, context);
    
    return {
      status: 'full_text_imported',
      localEntityId: entityId,
      fetchSource: 'semantic_scholar'
    };
  } catch (error) {
    log.error('[downloadAndImportPDF] ❌ Failed to download/import PDF');
    log.error(formatJSON({
      error: error instanceof Error ? {
        name: error.name,
        message: error.message,
        stack: error.stack
      } : error,
      pdfUrl,
      paperTitle: paper.title.substring(0, 100),
      folderId
    }, '⚠️ Error Context'));
    throw error;
  }
}

/**
 * Create PDF entity in dataspace
 */
async function createPDFEntity(
  paper: ResolvedPaper,
  pdfBlob: Blob,
  folderId: string,
  context: CommandExecutionContext
): Promise<string> {
  log.info('[createPDFEntity] 📝 Creating PDF entity...');
  
  const fileName = sanitizeFileName(paper.title) + '.pdf';
  log.info(formatJSON({
    fileName,
    fileSize: pdfBlob.size,
    fileType: pdfBlob.type,
    paperTitle: paper.title.substring(0, 100),
    folderId,
    spaceId: context.spaceId,
    doi: paper.doi
  }, '📤 FileService.addFile Request'));
  
  try {
    // Import FileService dynamically to avoid circular dependencies
    const { fileService } = await import('../../../services/FileService');
    
    // Create a File object from the blob with proper filename
    const file = new File([pdfBlob], fileName, { type: 'application/pdf' });
    
    // Upload using FileService (which will auto-trigger PDF enrichment)
    const uploadStart = Date.now();
    const entity = await fileService.addFile(file, folderId, context.spaceId);
    const uploadTime = Date.now() - uploadStart;
    
    log.info(formatJSON({
      entityId: entity._id,
      entityName: entity.name,
      entityType: entity.type,
      uploadTimeMs: uploadTime,
      willTriggerEnrichment: true
    }, '📥 FileService.addFile Response'));
    log.info('[createPDFEntity] ✅ PDF entity created successfully');
    
    return entity._id;
  } catch (error) {
    log.error('[createPDFEntity] ❌ Failed to create PDF entity');
    log.error(formatJSON({
      error: error instanceof Error ? {
        name: error.name,
        message: error.message,
        stack: error.stack
      } : error,
      fileName,
      fileSize: pdfBlob.size,
      folderId,
      spaceId: context.spaceId
    }, '⚠️ Error Context'));
    throw new Error(`Failed to create PDF entity: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Sanitize filename by removing invalid characters
 */
function sanitizeFileName(title: string): string {
  // Remove invalid filename characters and limit length
  return title
    .replace(/[<>:"/\\|?*\x00-\x1F]/g, '') // Remove invalid chars
    .replace(/\s+/g, ' ') // Normalize whitespace
    .trim()
    .substring(0, 200); // Limit length
}

/**
 * Create abstract-only entity
 */
async function createAbstractOnlyEntity(
  paper: ResolvedPaper,
  folderId: string,
  context: CommandExecutionContext
): Promise<string> {
  log.info('[createAbstractOnlyEntity] 📄 Creating abstract-only entity...');
  
  try {
    // Import entityStore to use proper entity creation
    const { default: useEntityStore } = await import('../../../stores/entityStore');
    const { createEntity } = useEntityStore.getState().actions;
    
    // Format the abstract document content
    const content = formatPaperMetadata(paper, true);
    
    const wordCount = content.trim().split(/\s+/).filter(Boolean).length;
    const charCount = content.length;
    
    const entityPayload = {
      entityType: 'Doc',
      name: `${paper.title} (Abstract Only)`,
      spaceId: context.spaceId,
      skeleton: {
        '@type': 'Doc',
        contentFormat: 'simple_text',
        content,
        source: 'citation_verification',
        groupIds: [folderId],
        wordCount,
        charCount,
        metadata: {
          doi: paper.doi,
          authors: paper.authors?.map(a => a.name).join(', '),
          year: paper.year,
          journal: paper.journal,
          citationCount: paper.citationCount,
          isAbstractOnly: true
        }
      }
    };
    
    log.info(formatJSON({
      ...entityPayload,
      skeleton: {
        ...entityPayload.skeleton,
        content: content.substring(0, 200) + '...' // Truncate content in logs
      }
    }, '📤 entityActions.createEntity Request'));
    
    // Create Doc entity with abstract using proper entityActions
    // This handles: backend call, store update, CRDT sync, event emission
    const entity = await createEntity(entityPayload);
    
    log.info(formatJSON({
      entityId: entity._id,
      entityName: entity.name,
      entityType: entity.type,
      wordCount,
      charCount
    }, '📥 entityActions.createEntity Response'));
    log.info('[createAbstractOnlyEntity] ✅ Abstract entity created successfully');
    
    return entity._id;
  } catch (error) {
    log.error('[createAbstractOnlyEntity] ❌ Failed to create abstract entity');
    log.error(formatJSON({
      error: error instanceof Error ? {
        name: error.name,
        message: error.message,
        stack: error.stack
      } : error,
      paperTitle: paper.title.substring(0, 100),
      hasAbstract: !!paper.abstract,
      folderId,
      spaceId: context.spaceId
    }, '⚠️ Error Context'));
    throw new Error(`Failed to create abstract entity: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Create metadata-only entity
 */
async function createMetadataOnlyEntity(
  paper: ResolvedPaper,
  folderId: string,
  context: CommandExecutionContext
): Promise<string> {
  log.info('[createMetadataOnlyEntity] 📋 Creating metadata-only entity...');
  
  try {
    // Import entityStore to use proper entity creation
    const { default: useEntityStore } = await import('../../../stores/entityStore');
    const { createEntity } = useEntityStore.getState().actions;
    
    // Format the metadata document content
    const content = formatPaperMetadata(paper, false);
    
    const wordCount = content.trim().split(/\s+/).filter(Boolean).length;
    const charCount = content.length;
    
    const entityPayload = {
      entityType: 'Doc',
      name: `${paper.title} (Metadata)`,
      spaceId: context.spaceId,
      skeleton: {
        '@type': 'Doc',
        contentFormat: 'simple_text',
        content,
        source: 'citation_verification',
        groupIds: [folderId],
        wordCount,
        charCount,
        metadata: {
          doi: paper.doi,
          authors: paper.authors?.map(a => a.name).join(', '),
          year: paper.year,
          journal: paper.journal,
          citationCount: paper.citationCount,
          isMetadataOnly: true
        }
      }
    };
    
    log.info(formatJSON({
      ...entityPayload,
      skeleton: {
        ...entityPayload.skeleton,
        content: content.substring(0, 200) + '...' // Truncate content in logs
      }
    }, '📤 entityActions.createEntity Request'));
    
    // Create Doc entity with metadata using proper entityActions
    // This handles: backend call, store update, CRDT sync, event emission
    const entity = await createEntity(entityPayload);
    
    log.info(formatJSON({
      entityId: entity._id,
      entityName: entity.name,
      entityType: entity.type,
      wordCount,
      charCount
    }, '📥 entityActions.createEntity Response'));
    log.info('[createMetadataOnlyEntity] ✅ Metadata entity created successfully');
    
    return entity._id;
  } catch (error) {
    log.error('[createMetadataOnlyEntity] ❌ Failed to create metadata entity');
    log.error(formatJSON({
      error: error instanceof Error ? {
        name: error.name,
        message: error.message,
        stack: error.stack
      } : error,
      paperTitle: paper.title.substring(0, 100),
      folderId,
      spaceId: context.spaceId
    }, '⚠️ Error Context'));
    throw new Error(`Failed to create metadata entity: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Format paper metadata as text content
 */
function formatPaperMetadata(paper: ResolvedPaper, includeAbstract: boolean): string {
  const lines: string[] = [];
  
  lines.push(`# ${paper.title}\n`);
  
  if (paper.authors && paper.authors.length > 0) {
    lines.push(`**Authors:** ${paper.authors.map(a => a.name).join(', ')}\n`);
  }
  
  if (paper.year) {
    lines.push(`**Year:** ${paper.year}\n`);
  }
  
  if (paper.journal) {
    lines.push(`**Journal:** ${paper.journal}\n`);
  }
  
  if (paper.doi) {
    lines.push(`**DOI:** ${paper.doi}\n`);
  }
  
  if (paper.citationCount !== undefined) {
    lines.push(`**Citations:** ${paper.citationCount}\n`);
  }
  
  if (paper.semanticScholarId) {
    lines.push(`**Semantic Scholar ID:** ${paper.semanticScholarId}\n`);
  }
  
  if (paper.isRetracted) {
    lines.push(`\n⚠️ **RETRACTED**`);
    if (paper.retractionReason) {
      lines.push(`\nRetraction Reason: ${paper.retractionReason}`);
    }
    lines.push('\n');
  }
  
  if (includeAbstract && paper.abstract) {
    lines.push(`\n## Abstract\n\n${paper.abstract}\n`);
  }
  
  if (paper.landingPageUrl) {
    lines.push(`\n**URL:** ${paper.landingPageUrl}\n`);
  }
  
  lines.push(`\n---\n\n*This document was created by the Citation Verification tool.*`);
  lines.push(`\n*Note: ${includeAbstract ? 'Full text was not available. Only abstract and metadata are included.' : 'Full text and abstract were not available. Only metadata is included.'}*`);
  
  return lines.join('\n');
}

